import { convertToUnit } from 'src/helpers/units_helpers';
import type { ChartConfig, ChartDataInterface, ChartConfigSeriesData } from 'src/types/Chart';

export type DeviceStatusValue = {
	name?: string;
	title?: string | number;
	value?: string | number;
	rawValue?: string | number;
	unit?: string;
};

const normalizeKey = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_');

const metricAliases = (chart: ChartConfig): string[] => {
	const metric = normalizeKey(chart.metric || chart.name);
	const aliases = new Set([
		metric,
		normalizeKey(chart.name),
		normalizeKey(chart.title),
		normalizeKey(chart.sensorID),
		normalizeKey(chart.extid),
	]);

	if (metric === 'consumption') {
		aliases.add('power');
		aliases.add('apower');
	}
	if (metric === 'wind_speed') {
		aliases.add('speed');
	}
	if (metric === 'frequency') {
		aliases.add('freq_measurement');
	}

	return [...aliases].filter(Boolean);
};

const statusKey = (status: DeviceStatusValue): string => normalizeKey(status.name).split(':')[0];

const isUnavailable = (value: unknown): boolean => {
	if (value == null || value === '') {
		return true;
	}
	if (typeof value === 'number') {
		return !Number.isFinite(value);
	}
	return /^(n\/a|na|unknown|undefined|null)(\s|$)/i.test(String(value).trim());
};

const formatStatusValue = (status: DeviceStatusValue, fallbackUnit: string): string | null => {
	const displayValue = status.value ?? status.title;
	if (isUnavailable(displayValue)) {
		return null;
	}
	if (typeof displayValue === 'number') {
		return convertToUnit(displayValue, status.unit || fallbackUnit).trim();
	}
	return String(displayValue).trim();
};

export const resolveRuntimeChartValue = (
	statuses: DeviceStatusValue[],
	chart: ChartConfig,
): string | null => {
	const aliases = metricAliases(chart);
	const channelIds = [chart.sensorID, chart.extid, chart.deviceChannel]
		.map(normalizeKey)
		.filter(Boolean);
	const match = statuses
		.map((status) => {
			const key = statusKey(status);
			const fullName = normalizeKey(status.name);
			const channelMatch = aliases.some((alias) =>
				channelIds.some(
					(channel) =>
						fullName === `${alias}:${channel}` || fullName === `${alias}_${channel}`,
				),
			);
			const score = channelMatch
				? 0
				: aliases.some((alias) => fullName === alias)
					? 1
					: aliases.some((alias) => key === alias)
						? 2
						: Number.POSITIVE_INFINITY;
			return { status, score };
		})
		.filter(({ score }) => Number.isFinite(score))
		.sort((a, b) => a.score - b.score)[0]?.status;

	return match ? formatStatusValue(match, chart.units) : null;
};

const seriesPriority = (name: string, metric: string): number => {
	const normalized = normalizeKey(name);
	if (/(^|_)(avg|average|current|value)($|_)/.test(normalized)) {
		return 0;
	}
	if (normalized === metric) {
		return 1;
	}
	if (!/(^|_)(min|max|minimum|maximum)($|_)/.test(normalized)) {
		return 2;
	}
	if (/(^|_)(max|maximum)($|_)/.test(normalized)) {
		return 3;
	}
	return 4;
};

const latestValidPoint = (data: ChartConfigSeriesData[] = []): ChartConfigSeriesData | null =>
	data.reduce<ChartConfigSeriesData | null>((latest, point) => {
		const value = Number(point?.value);
		if (point?.missing || point?.value == null || !Number.isFinite(value)) {
			return latest;
		}
		if (!latest || Number(point.dt ?? 0) >= Number(latest.dt ?? 0)) {
			return point;
		}
		return latest;
	}, null);

export const resolveLatestPlottedValue = (chartData: ChartDataInterface): string | null => {
	const metric = normalizeKey(chartData.metric || chartData.name);
	const candidates = (chartData.series || [])
		.filter((series) => series.visible !== false && !series.prevPeriod)
		.map((series) => ({
			series,
			point: latestValidPoint(series.data),
			priority: seriesPriority(series.name, metric),
		}))
		.filter((candidate) => candidate.point)
		.sort((a, b) => a.priority - b.priority || Number(b.point?.dt ?? 0) - Number(a.point?.dt ?? 0));

	const value = Number(candidates[0]?.point?.value);
	return Number.isFinite(value) ? convertToUnit(value, chartData.units).trim() : null;
};

export const resolveLatestChartValue = (
	statuses: DeviceStatusValue[],
	chart: ChartConfig,
	chartData?: ChartDataInterface,
): string | null =>
	resolveRuntimeChartValue(statuses, chart) ||
	(chartData ? resolveLatestPlottedValue(chartData) : null);
